import React, { useState } from 'react';
import THEME from '../utils/theme';
import { CAT_META } from '../utils/helpers';
import useFetch from '../hooks/useFetch';
import useInView from '../hooks/useInView';

const EMPTY_FORM = { name: '', phone: '', email: '', address: '', courseInterest: '', batchRef: '', message: '' };

export default function Apply() {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const { data: batches } = useFetch(`${API_BASE}/batches`);
  const { data: courses } = useFetch(`${API_BASE}/courses`);
  const [ref, visible] = useInView();
  const [form, setForm]           = useState(EMPTY_FORM);
  const [status, setStatus]       = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const activeBatches = batches.filter(b => b.status !== 'completed');
  const activeCourses = courses.filter(c => c.isActive);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.courseInterest) {
      setStatus({ type: 'error', msg: 'Full name, phone number, and course selection are required.' });
      return;
    }
    setSubmitting(true);
    setStatus(null);
    try {
      const payload = { ...form };
      if (!payload.batchRef) delete payload.batchRef;
      const res = await fetch(`${API_BASE}/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus({ type: 'success', msg: '✅ Your application has been submitted! Our team will contact you within 24 hours.' });
        setForm(EMPTY_FORM);
      } else {
        setStatus({ type: 'error', msg: data.message || 'Something went wrong. Please try again.' });
      }
    } catch {
      setStatus({ type: 'error', msg: 'Could not connect to the server. Please try again later.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)', color: 'white' };

  return (
    <section id="apply" style={{
      padding: '100px clamp(20px,4vw,40px)',
      background: `linear-gradient(135deg, ${THEME.navy} 0%, #162236 100%)`,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', border: `1px solid ${THEME.red}20`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -60, left: -60, width: 300, height: 300, borderRadius: '50%', border: `1px solid ${THEME.green}20`, pointerEvents: 'none' }} />

      <div style={{ maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div ref={ref} className={`sk-fade-up ${visible ? 'sk-visible' : ''}`} style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: `${THEME.green}25`, border: `1px solid ${THEME.green}40`,
            color: '#6FD9BC', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
            textTransform: 'uppercase', padding: '6px 16px', borderRadius: 2, marginBottom: 20,
            fontFamily: "'Syne', sans-serif",
          }}>Application Form</div>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 'clamp(2rem,4vw,3.2rem)', lineHeight: 1.1, color: 'white' }}>
            Apply Today
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 16, marginTop: 14, lineHeight: 1.75 }}>
            Fill in the form below — our team will get back to you within 24 hours.
          </p>
        </div>

        <form onSubmit={handleSubmit}
          className={`sk-fade-up ${visible ? 'sk-visible' : ''}`}
          style={{
            transitionDelay: '0.15s',
            background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16,
            padding: 'clamp(28px,5vw,48px)',
          }}>

          <div className="sk-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)' }}>Full Name *</label>
              <input name="name" value={form.name} onChange={handleChange} placeholder="Enter your full name" style={inputStyle} />
            </div>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)' }}>Phone Number *</label>
              <input name="phone" value={form.phone} onChange={handleChange} placeholder="+880-1X-XXXXXXXX" style={inputStyle} />
            </div>
          </div>

          <div className="sk-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)' }}>Email (Optional)</label>
              <input name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" type="email" style={inputStyle} />
            </div>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)' }}>Address</label>
              <input name="address" value={form.address} onChange={handleChange} placeholder="District / Upazila" style={inputStyle} />
            </div>
          </div>

          <div className="sk-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)' }}>Course of Interest *</label>
              <select name="courseInterest" value={form.courseInterest} onChange={handleChange}
                style={{ ...inputStyle, color: form.courseInterest ? 'white' : 'rgba(255,255,255,0.4)' }}>
                <option value="">Select a course</option>
                {activeCourses.length > 0
                  ? activeCourses.map(c => (
                      <option key={c._id} value={c.name} style={{ background: THEME.navy }}>{c.name}</option>
                    ))
                  : Object.entries(CAT_META).map(([k, v]) => (
                      // No emoji here — just label text
                      <option key={k} value={v.label} style={{ background: THEME.navy }}>{v.label}</option>
                    ))
                }
              </select>
            </div>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)' }}>Preferred Batch (Optional)</label>
              <select name="batchRef" value={form.batchRef} onChange={handleChange}
                style={{ ...inputStyle, color: form.batchRef ? 'white' : 'rgba(255,255,255,0.4)' }}>
                <option value="">Select a batch</option>
                {activeBatches.map(b => (
                  <option key={b._id} value={b._id} style={{ background: THEME.navy }}>
                    {b.batchName} ({b.status === 'upcoming' ? 'Upcoming' : 'Ongoing'})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 32 }}>
            <label style={{ color: 'rgba(255,255,255,0.7)' }}>Message (Optional)</label>
            <textarea name="message" value={form.message} onChange={handleChange}
              rows={4} placeholder="Any questions or comments..." style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          {status && (
            <div style={{
              padding: '14px 20px', borderRadius: 8, marginBottom: 24,
              background: status.type === 'success' ? `${THEME.green}25` : `${THEME.red}25`,
              border: `1px solid ${status.type === 'success' ? THEME.green : THEME.red}40`,
              color: status.type === 'success' ? '#6FD9BC' : '#FF8096',
              fontSize: 14, lineHeight: 1.6,
            }}>{status.msg}</div>
          )}

          <button type="submit" disabled={submitting} style={{
            width: '100%', background: submitting ? '#555' : THEME.red, color: 'white',
            border: 'none', padding: '16px 32px', borderRadius: 6,
            fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 16,
            cursor: submitting ? 'not-allowed' : 'pointer', letterSpacing: '0.04em',
            transition: 'background 0.2s',
          }}
            onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = THEME.redDark; }}
            onMouseLeave={e => { if (!submitting) e.currentTarget.style.background = THEME.red; }}>
            {submitting ? 'Submitting...' : 'Submit Application →'}
          </button>
        </form>
      </div>

      <style>{`@media (max-width: 640px) { .sk-form-row { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}
